import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderKanban,
  MapPin,
  Plus,
  Tag as TagIcon,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { EventDialog } from '@/components/calendar/EventDialog';
import { useProjects } from '@/features/projects/hooks';
import {
  useCalendarEvents,
  useCalendarTags,
  useMyCalendarTasks,
  type CalendarEventWithDetails,
  type CalendarTaskRow,
} from '@/features/calendar/hooks';
import {
  startGoogleCalendarOAuth,
  useDisconnectGoogleCalendar,
  useGoogleCalendarConnection,
  useGoogleCalendarEvents,
  type GoogleCalendarOccurrence,
} from '@/features/calendar/googleCalendar';
import { CALENDAR_CHIP_CLASS, CALENDAR_DOT_CLASS } from '@/features/calendar/colors';
import { cn } from '@/lib/utils';
import {
  formatWorkloadHours,
  isHeavyDay,
  sumWorkloadHours,
  WORKDAY_HOURS,
} from '@/features/projects/workload';

// ---------------------------------------------------------------------------
// Local-date helpers. The calendar works entirely in LOCAL `YYYY-MM-DD`
// keys — event_date / due_date are date columns, so no timezone math.
// ---------------------------------------------------------------------------

function toKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function formatTimeLabel(time: string): string {
  const [hStr, mStr] = time.split(':');
  let h = Number(hStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return mStr === '00' ? `${h} ${suffix}` : `${h}:${mStr} ${suffix}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

/** Sentinel id for the "events without a tag" filter row. */
const UNTAGGED = '__untagged__';

// ---------------------------------------------------------------------------
// localStorage-backed preferences
// ---------------------------------------------------------------------------

const SHOW_TASKS_KEY = 'atlas:calendar:showProjectTasks';
const DISABLED_PROJECTS_KEY = 'atlas:calendar:disabledProjects';
const HIDDEN_TAGS_KEY = 'atlas:calendar:hiddenTags';
const SHOW_GOOGLE_KEY = 'atlas:calendar:showGoogleCalendar';

function readBoolPref(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Like readBoolPref but defaults ON until the user explicitly turns it
 *  off — used for the Google overlay so events appear right after
 *  connecting without an extra toggle. */
function readBoolPrefDefaultOn(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== '0';
  } catch {
    return true;
  }
}

function readSetPref(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function writePref(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota / private mode — in-memory state stays authoritative */
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CalendarPage() {
  const navigate = useNavigate();

  // Month being viewed — defaults to the month the user logs in on.
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [showProjectTasks, setShowProjectTasks] = useState(() =>
    readBoolPref(SHOW_TASKS_KEY),
  );
  // Disabled (not enabled) sets so newly-created projects / tags default
  // to visible without a migration of the stored preference.
  const [disabledProjects, setDisabledProjects] = useState<Set<string>>(() =>
    readSetPref(DISABLED_PROJECTS_KEY),
  );
  const [hiddenTags, setHiddenTags] = useState<Set<string>>(() =>
    readSetPref(HIDDEN_TAGS_KEY),
  );
  const [showGoogle, setShowGoogle] = useState(() =>
    readBoolPrefDefaultOn(SHOW_GOOGLE_KEY),
  );

  // Dialog state — either editing an existing event or creating one
  // (optionally pre-filled with the clicked day).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventWithDetails | null>(null);
  const [createDate, setCreateDate] = useState<string | undefined>(undefined);

  // The visible grid spans from the Sunday on/before the 1st to the
  // Saturday on/after the last day of the month.
  const { weeks, range } = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = addDays(first, -first.getDay());
    const end = addDays(last, 6 - last.getDay());
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    const wk: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) wk.push(days.slice(i, i + 7));
    return {
      weeks: wk,
      range: { start: toKey(start), end: toKey(end) },
    };
  }, [cursor]);

  const { data: events, isLoading: eventsLoading } = useCalendarEvents(range);
  const { data: tags } = useCalendarTags();
  const { data: myTasks } = useMyCalendarTasks(range, showProjectTasks);
  const { data: googleConnection } = useGoogleCalendarConnection();
  const googleConnected = googleConnection?.status === 'connected';
  const { data: googleEvents } = useGoogleCalendarEvents(
    range,
    googleConnected && showGoogle,
  );
  const disconnectGoogle = useDisconnectGoogleCalendar();
  const { data: allProjects } = useProjects({ includeArchived: false });
  const projects = useMemo(
    () => (allProjects ?? []).filter((p) => !p.archived_at),
    [allProjects],
  );

  const visibleEventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventWithDetails[]>();
    for (const ev of events ?? []) {
      if (hiddenTags.has(ev.tag_id ?? UNTAGGED)) continue;
      const list = map.get(ev.event_date);
      if (list) list.push(ev);
      else map.set(ev.event_date, [ev]);
    }
    return map;
  }, [events, hiddenTags]);

  const visibleTasksByDay = useMemo(() => {
    const map = new Map<string, CalendarTaskRow[]>();
    if (!showProjectTasks) return map;
    for (const task of myTasks ?? []) {
      if (disabledProjects.has(task.project.id)) continue;
      const list = map.get(task.due_date);
      if (list) list.push(task);
      else map.set(task.due_date, [task]);
    }
    return map;
  }, [myTasks, showProjectTasks, disabledProjects]);

  const googleEventsByDay = useMemo(() => {
    const map = new Map<string, GoogleCalendarOccurrence[]>();
    if (!showGoogle) return map;
    for (const occ of googleEvents ?? []) {
      const list = map.get(occ.dateKey);
      if (list) list.push(occ);
      else map.set(occ.dateKey, [occ]);
    }
    return map;
  }, [googleEvents, showGoogle]);

  const todayStr = toKey(new Date());
  const hiddenTagCount = hiddenTags.size;
  const disabledProjectCount = disabledProjects.size;

  const toggleShowTasks = (checked: boolean) => {
    setShowProjectTasks(checked);
    writePref(SHOW_TASKS_KEY, checked ? '1' : '0');
  };

  const toggleProject = (projectId: string) => {
    setDisabledProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      writePref(DISABLED_PROJECTS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const toggleTag = (tagId: string) => {
    setHiddenTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      writePref(HIDDEN_TAGS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const toggleShowGoogle = (checked: boolean) => {
    setShowGoogle(checked);
    writePref(SHOW_GOOGLE_KEY, checked ? '1' : '0');
  };

  const handleConnectGoogle = () => {
    const err = startGoogleCalendarOAuth();
    if (err) toast.error(err);
  };

  const handleDisconnectGoogle = () => {
    disconnectGoogle.mutate(undefined, {
      onSuccess: () => toast.success('Google Calendar disconnected.'),
      onError: (e) =>
        toast.error(e instanceof Error ? e.message : 'Disconnect failed.'),
    });
  };

  const openCreate = (date?: string) => {
    setEditingEvent(null);
    setCreateDate(date);
    setDialogOpen(true);
  };

  const openEdit = (ev: CalendarEventWithDetails) => {
    setEditingEvent(ev);
    setCreateDate(undefined);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Calendar"
        actions={
          <Button onClick={() => openCreate()}>
            <Plus className="h-4 w-4" />
            Add event
          </Button>
        }
      />

      {/* ───────────── Toolbar ───────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const now = new Date();
              setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <span className="min-w-36 text-sm font-medium text-foreground">
          {MONTH_LABEL.format(cursor)}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Tag visibility filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <TagIcon className="h-3.5 w-3.5" />
                Tags
                {hiddenTagCount > 0 && (
                  <span className="rounded-full bg-secondary px-1.5 text-[10px] text-secondary-foreground">
                    {hiddenTagCount} hidden
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Show events tagged…</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={!hiddenTags.has(UNTAGGED)}
                onCheckedChange={() => toggleTag(UNTAGGED)}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="text-muted-foreground">No tag</span>
              </DropdownMenuCheckboxItem>
              {(tags ?? []).length > 0 && <DropdownMenuSeparator />}
              {(tags ?? []).map((tag) => (
                <DropdownMenuCheckboxItem
                  key={tag.id}
                  checked={!hiddenTags.has(tag.id)}
                  onCheckedChange={() => toggleTag(tag.id)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        CALENDAR_DOT_CLASS[tag.color],
                      )}
                    />
                    {tag.name}
                  </span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Per-project filter, only meaningful while tasks are shown */}
          {showProjectTasks && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <FolderKanban className="h-3.5 w-3.5" />
                  Projects
                  {disabledProjectCount > 0 && (
                    <span className="rounded-full bg-secondary px-1.5 text-[10px] text-secondary-foreground">
                      {disabledProjectCount} hidden
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Show tasks from…</DropdownMenuLabel>
                {projects.length === 0 ? (
                  <p className="px-2 pb-2 pt-1 text-xs text-muted-foreground">
                    No projects yet.
                  </p>
                ) : (
                  projects.map((p) => (
                    <DropdownMenuCheckboxItem
                      key={p.id}
                      checked={!disabledProjects.has(p.id)}
                      onCheckedChange={() => toggleProject(p.id)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className="truncate">{p.name}</span>
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm text-foreground">
            <Switch checked={showProjectTasks} onCheckedChange={toggleShowTasks} />
            <span className="flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
              Project tasks
            </span>
          </label>

          {/* Per-user Google Calendar overlay — connect / show / disconnect */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <CalendarDays className="h-3.5 w-3.5" />
                Google
                {googleConnection?.status === 'error' && (
                  <span className="rounded-full bg-destructive/10 px-1.5 text-[10px] text-destructive">
                    reconnect
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {googleConnection ? (
                <>
                  <DropdownMenuLabel className="truncate">
                    {googleConnection.google_email ?? 'Google Calendar'}
                  </DropdownMenuLabel>
                  {googleConnection.status === 'error' ? (
                    <>
                      <p className="px-2 pb-1 text-xs text-muted-foreground">
                        The connection stopped working — reconnect to keep
                        seeing your Google events.
                      </p>
                      <DropdownMenuItem onSelect={handleConnectGoogle}>
                        Reconnect Google Calendar…
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuCheckboxItem
                      checked={showGoogle}
                      onCheckedChange={toggleShowGoogle}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Show my Google events
                    </DropdownMenuCheckboxItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={disconnectGoogle.isPending}
                    onSelect={handleDisconnectGoogle}
                  >
                    Disconnect
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuLabel>Google Calendar</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={handleConnectGoogle}>
                    Connect Google Calendar…
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ───────────── Month grid ───────────── */}
      <div className="overflow-x-auto">
        <div className="min-w-[860px] overflow-hidden rounded-xl border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          {eventsLoading ? (
            <div className="grid grid-cols-7 gap-px bg-border">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="min-h-28 bg-card p-2">
                  <Skeleton className="mb-2 h-4 w-6" />
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
            </div>
          ) : (
            weeks.map((week, wi) => (
              <div
                key={wi}
                className={cn(
                  'grid grid-cols-7 gap-px bg-border',
                  wi > 0 && 'border-t border-border',
                )}
              >
                {week.map((day) => {
                  const key = toKey(day);
                  const inMonth = day.getMonth() === cursor.getMonth();
                  const isToday = key === todayStr;
                  const dayEvents = visibleEventsByDay.get(key) ?? [];
                  const dayTasks = visibleTasksByDay.get(key) ?? [];
                  const dayGoogle = googleEventsByDay.get(key) ?? [];
                  const dayHours = sumWorkloadHours(dayTasks);
                  const heavy = isHeavyDay(dayHours);
                  return (
                    <div
                      key={key}
                      role="button"
                      tabIndex={0}
                      aria-label={`Add event on ${key}`}
                      onClick={() => openCreate(key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') openCreate(key);
                      }}
                      className={cn(
                        'group min-h-28 cursor-pointer space-y-1 p-1.5 transition-colors',
                        inMonth ? 'bg-card hover:bg-secondary/30' : 'bg-secondary/20 hover:bg-secondary/40',
                      )}
                    >
                      <div className="flex items-center justify-between px-0.5">
                        <span
                          className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                            isToday
                              ? 'bg-primary font-semibold text-primary-foreground'
                              : inMonth
                                ? 'text-foreground'
                                : 'text-muted-foreground/60',
                          )}
                        >
                          {day.getDate()}
                        </span>
                        {dayHours > 0 ? (
                          <span
                            className={cn(
                              'text-[10px] font-medium',
                              heavy
                                ? 'text-fresh-salmon-dark'
                                : 'text-muted-foreground',
                            )}
                            title={
                              heavy
                                ? `${formatWorkloadHours(dayHours)} estimated — over a typical ${WORKDAY_HOURS}h day`
                                : `${formatWorkloadHours(dayHours)} estimated on your tasks`
                            }
                          >
                            {formatWorkloadHours(dayHours)}
                          </span>
                        ) : (
                          <Plus className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-60" />
                        )}
                      </div>

                      {dayEvents.map((ev) => (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(ev);
                          }}
                          className={cn(
                            'block w-full truncate rounded-md border-l-2 px-1.5 py-1 text-left text-xs transition-colors',
                            CALENDAR_CHIP_CLASS[ev.color],
                          )}
                          title={[
                            ev.title,
                            ev.start_time && formatTimeLabel(ev.start_time.slice(0, 5)),
                            ev.location && `@ ${ev.location}`,
                            ev.tag?.name && `#${ev.tag.name}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        >
                          {ev.start_time && (
                            <span className="mr-1 font-medium opacity-75">
                              {formatTimeLabel(ev.start_time.slice(0, 5))}
                            </span>
                          )}
                          {ev.title}
                          {(ev.attendees.length > 0 || ev.location) && (
                            <span className="ml-1 inline-flex items-center gap-1 align-middle opacity-60">
                              {ev.attendees.length > 0 && <Users className="h-3 w-3" />}
                              {ev.location && <MapPin className="h-3 w-3" />}
                            </span>
                          )}
                        </button>
                      ))}

                      {dayGoogle.map((g) => (
                        <button
                          key={g.key}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (g.htmlLink) {
                              window.open(g.htmlLink, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          className="block w-full truncate rounded-md border-l-2 border-sky-500 bg-sky-50 px-1.5 py-1 text-left text-xs text-sky-950 transition-colors hover:bg-sky-100"
                          title={[
                            `Google Calendar · ${g.title}`,
                            g.startTime && formatTimeLabel(g.startTime),
                            g.location && `@ ${g.location}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        >
                          <CalendarDays className="mr-1 inline h-3 w-3 align-[-2px] opacity-60" />
                          {g.startTime && (
                            <span className="mr-1 font-medium opacity-75">
                              {formatTimeLabel(g.startTime)}
                            </span>
                          )}
                          {g.title}
                        </button>
                      ))}

                      {dayTasks.map((task) => {
                        const hoursLabel = formatWorkloadHours(
                          task.approximate_workload_hours,
                        );
                        return (
                        <button
                          key={task.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${task.project.id}`);
                          }}
                          className="block w-full truncate rounded-md border border-dashed border-border bg-secondary/50 px-1.5 py-1 text-left text-xs text-secondary-foreground transition-colors hover:bg-secondary"
                          title={[
                            `Task · ${task.name} — ${task.project.name}`,
                            hoursLabel || null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        >
                          <ClipboardList className="mr-1 inline h-3 w-3 align-[-2px] opacity-60" />
                          {task.name}
                          {hoursLabel && (
                            <span className="ml-1 text-[10px] opacity-70">
                              {hoursLabel}
                            </span>
                          )}
                        </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {!eventsLoading && (events ?? []).length === 0 && (
        <p className="flex items-center justify-center gap-2 pb-4 text-sm text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          No events this month yet.
        </p>
      )}

      <EventDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditingEvent(null);
        }}
        event={editingEvent}
        defaultDate={createDate}
      />
    </div>
  );
}
